import React, { useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { AlertCircle, CheckCircle2, Loader2, MessageSquare, Send } from 'lucide-react'
import { functions } from '../firebase-config'

const inputClassName = 'w-full rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/50'

const asuntos = {
  consulta: 'Consulta general',
  reclamo: 'Reclamo de calculo',
  error: 'Reporte de error',
  sugerencia: 'Sugerencia de mejora',
  otro: 'Otro',
}

export default function ContactForm({ onClose }) {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: 'consulta',
    mensaje: '',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState('')
  const [ticketCode, setTicketCode] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess('')
    setTicketCode('')

    if (!formData.nombre || !formData.email || !formData.mensaje) {
      setError('Nombre, email y mensaje son obligatorios')
      return
    }
    if (!formData.email.includes('@')) {
      setError('Email invalido')
      return
    }
    if (formData.mensaje.trim().length < 10) {
      setError('El mensaje debe tener al menos 10 caracteres')
      return
    }

    setIsLoading(true)
    try {
      const callCreate = httpsCallable(functions, 'createGeneralSupportTicket')
      const asuntoLabel = asuntos[formData.asunto] || 'Consulta'
      const resp = await callCreate({
        asunto: `[${asuntoLabel}] ${formData.nombre}`,
        detalle: formData.mensaje.trim(),
        nombre: formData.nombre.trim(),
        email: formData.email.trim(),
        telefono: formData.telefono.trim(),
        userId: localStorage.getItem('userRef') || null,
      })

      const code = resp?.data?.ticketCode || 'OK'
      setTicketCode(code)
      setSuccess('Ticket generado correctamente')
      setFormData({ nombre: '', email: '', telefono: '', asunto: 'consulta', mensaje: '' })
    } catch (err) {
      setError(err?.message || 'No se pudo generar el ticket de soporte')
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="rounded-3xl border border-emerald-300/30 bg-emerald-500/10 p-6 text-center shadow-[0_20px_50px_rgba(0,0,0,0.3)] backdrop-blur-2xl md:p-8">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-emerald-300/40 bg-emerald-500/20">
          <CheckCircle2 className="h-6 w-6 text-emerald-200" />
        </div>
        <h3 className="text-2xl font-bold text-emerald-100">Ticket enviado a soporte</h3>
        <p className="mt-2 text-sm text-emerald-50/90">Tu consulta ya esta en la cola del panel administrador.</p>
        <p className="mt-4 text-sm text-slate-200">Codigo de ticket:</p>
        <p className="mt-1 inline-block rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-base font-semibold text-white">
          {ticketCode}
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={() => onClose && onClose()}
            className="rounded-xl border border-white/30 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20"
          >
            Cerrar ventana
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-3xl border border-white/20 bg-white/10 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.3)] backdrop-blur-2xl md:p-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-full border border-cyan-300/40 bg-cyan-500/20 p-2">
          <MessageSquare className="h-5 w-5 text-cyan-200" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-white">Generar ticket de soporte</h2>
          <p className="text-sm text-slate-300">Se crea en la cola de tickets del panel admin.</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex gap-2 rounded-xl border border-red-400/40 bg-red-500/15 p-3 text-sm text-red-100">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 flex gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/15 p-3 text-sm text-emerald-100">
          <CheckCircle2 className="mt-0.5 h-4 w-4" />
          <span>{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-2 block text-sm text-slate-200">Nombre completo *</label>
          <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} placeholder="Tu nombre" className={inputClassName} disabled={isLoading} />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm text-slate-200">Email *</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="tu@email.com" className={inputClassName} disabled={isLoading} />
          </div>
          <div>
            <label className="mb-2 block text-sm text-slate-200">Telefono</label>
            <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange} placeholder="+54 9 ..." className={inputClassName} disabled={isLoading} />
          </div>
        </div>

        <div>
          <label className="mb-2 block text-sm text-slate-200">Tipo de consulta *</label>
          <select name="asunto" value={formData.asunto} onChange={handleChange} className={inputClassName} disabled={isLoading}>
            {Object.entries(asuntos).map(([key, label]) => (
              <option key={key} value={key} className="bg-slate-900">{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-2 block text-sm text-slate-200">Mensaje *</label>
          <textarea
            name="mensaje"
            value={formData.mensaje}
            onChange={handleChange}
            placeholder="Describe tu consulta en detalle..."
            rows={6}
            className={`${inputClassName} resize-none`}
            disabled={isLoading}
          />
          <p className="mt-1 text-xs text-slate-400">{formData.mensaje.length} caracteres</p>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 py-3 font-semibold text-slate-950 transition hover:brightness-110 disabled:opacity-70"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Generando ticket...
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              Enviar a soporte
            </>
          )}
        </button>
      </form>
    </div>
  )
}
